import pino, { LoggerOptions } from 'pino';
import { readEnv } from '../config/env-access';

// Log level: default to info; allow override via LOG_LEVEL or BOLT_LOG_LEVEL
const level = (readEnv('LOG_LEVEL') || readEnv('BOLT_LOG_LEVEL') || 'info').toLowerCase();

// Pretty printing toggle. Default ON; set LOG_PRETTY=0 or LOG_PRETTY=false to disable.
const prettyEnv = String(readEnv('LOG_PRETTY') || '').toLowerCase();
const usePretty = !(prettyEnv === '0' || prettyEnv === 'false');

// Error log: write warn/error/fatal to a dedicated file for agent consumption.
// Path comes from pm2 log prefix convention or defaults to /tmp.
const logPrefix = readEnv('PM2_LOG_PREFIX') || '/tmp/slack-dev';
const errorLogPath = `${logPrefix}-errors-only.log`;

const prettyOpts = {
  colorize: true,
  customColors: 'trace:cyan,debug:green,info:blue,warn:yellow,error:red,fatal:magenta',
  translateTime: 'SYS:standard',
  singleLine: true,
  levelFirst: true,
  messageKey: 'msg',
};

// `err.config.headers.*`: pino's err serializer copies an AxiosError's `config`, whose headers hold
// the outbound credential (the Clay client sends `Authorization: <token>`).
export const LOGGER_REDACT_PATHS = [
  'headers.authorization',
  'authorization',
  'token',
  'body.token',
  'body.headers.authorization',
  'err.config.headers.Authorization',
  'err.config.headers.authorization',
  '*.config.headers.Authorization',
  '*.config.headers.authorization',
];

const baseOptions: LoggerOptions = {
  level,
  base: null, // do not include pid/hostname
  redact: {
    paths: LOGGER_REDACT_PATHS,
    censor: '[REDACTED]',
  },
};

// Every pino transport (pino-pretty or pino/file) runs in a dedicated
// thread-stream worker thread, even for a single target. Under `bun test
// --isolate`, this module re-executes per isolated test file, so ~580 test
// files would each spawn fresh worker threads in the same CI run — thread/fd
// exhaustion that surfaces as an intermittent "the worker thread exited"
// crash in thread-stream, which then corrupts the bun test runner state for
// unrelated files. Tests get a synchronous stdout destination instead: no
// worker, same serialization/redaction, since nothing in this repo's tests
// asserts on the real transport's rendered output or file routing.
export const logger =
  readEnv('NODE_ENV') === 'test'
    ? pino(baseOptions, pino.destination({ dest: 1, sync: true }))
    : pino({
        ...baseOptions,
        // Note: formatters.level cannot be used with transport.targets (pino
        // runs transports in worker threads and can't serialize functions).
        // pino-pretty already outputs the level label by default, so this is
        // fine without it.
        transport: {
          targets: [
            // Main log: all levels, pretty-printed in dev
            ...(usePretty
              ? [{ target: 'pino-pretty', options: prettyOpts, level }]
              : [{ target: 'pino/file', options: { destination: 1 }, level }]),
            // Error-only log: warn+ to a dedicated file (machine-readable, no colors)
            {
              target: 'pino/file',
              options: { destination: errorLogPath, mkdir: true },
              level: 'warn',
            },
          ],
        },
      });

